import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Claim, FormulaBreakdown, PensionAccount, PensionStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function hasAny(roles: string[], ...wanted: string[]): boolean {
  return wanted.some((w) => roles.includes(w));
}

export function StaffBenefitClaims() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canOperate = hasAny(roles, 'SPF_BENEFITS_OFFICER', 'SPF_ADMIN', 'SPF_APPROVER_A', 'SPF_APPROVER_B');
  const isOfficer = hasAny(roles, 'SPF_BENEFITS_OFFICER', 'SPF_ADMIN');
  const isAdmin = roles.includes('SPF_ADMIN');

  if (!canOperate) {
    return (
      <div>
        <PageHeader title="Benefit Claims" subtitle="Claims, awards & the pensioner roll (P03)" />
        <ErrorNote message="Your role does not include a P03 benefits role — this page is unavailable." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Benefit Claims" subtitle="Claims, awards, pay runs & the pensioner roll (P03)" />

      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        P03 has no back-office claim list/search endpoint — only the citizen's own <code>/me/claims</code>. Process a
        claim below by pasting in its reference ID (the member gives you this, e.g. from their acknowledgement or
        the PCL-xxxx reference number shown on their submission).
      </div>

      {isOfficer ? <PensionerRoll /> : null}
      <ClaimWorkbench canVerify={isOfficer} canApprove={hasAny(roles, 'SPF_APPROVER_A', 'SPF_APPROVER_B')} approverRoles={roles} />
      {isOfficer ? <PayRunPanel /> : null}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {isOfficer ? <MaintenancePanel /> : null}
        {isAdmin ? <AnnualRecalcPanel /> : null}
      </div>
      {isOfficer ? <RefundPanel /> : null}
    </div>
  );
}

function PensionerRoll() {
  const [status, setStatus] = useState<PensionStatus>('IN_PAYMENT');
  const rollQ = useApiQuery<{ items: PensionAccount[] }>(`/api/v1/benefits/pensioners?status=${status}`);

  const act = async (id: string, action: 'suspend' | 'resume') => {
    try {
      if (action === 'suspend') {
        const reason = window.prompt('Reason for suspending this pension?');
        if (!reason) return;
        await api.post(`/api/v1/benefits/accounts/${id}/suspend`, { reason });
      } else {
        await api.post(`/api/v1/benefits/accounts/${id}/resume`);
      }
      rollQ.reload();
    } catch {
      // reload reflects current state regardless
    }
  };

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Pensioner roll</h2>
        <select className="field-input max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value as PensionStatus)}>
          <option value="IN_PAYMENT">In payment</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="CEASED">Ceased</option>
        </select>
      </div>
      <div className="card overflow-x-auto">
        {rollQ.loading ? (
          <Spinner />
        ) : (rollQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No accounts in this status" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Type</th>
                <th className="table-head text-right">Monthly amount</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {rollQ.data!.items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">{a.nin}</td>
                  <td className="table-cell">{a.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-right tabular-nums">{scr(a.monthlyAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                    {a.suspendReason ? <div className="mt-0.5 text-[11px] text-slate-400">{a.suspendReason}</div> : null}
                  </td>
                  <td className="table-cell text-right">
                    {a.status === 'IN_PAYMENT' ? (
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act(a.id, 'suspend')}>
                        Suspend
                      </button>
                    ) : a.status === 'SUSPENDED' ? (
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(a.id, 'resume')}>
                        Resume
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

type WorkbenchResult =
  | { kind: 'claim'; claim: Claim }
  | { kind: 'calc'; claim: Claim; breakdown: FormulaBreakdown }
  | { kind: 'account'; account: PensionAccount };

function ClaimWorkbench({
  canVerify,
  canApprove,
  approverRoles,
}: {
  canVerify: boolean;
  canApprove: boolean;
  approverRoles: string[];
}) {
  const [claimId, setClaimId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dob, setDob] = useState('');
  const [result, setResult] = useState<WorkbenchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (action: 'verify-docs' | 'calculate' | 'award') => {
    if (!claimId) {
      setError('Enter a claim ID first.');
      return;
    }
    setBusy(action);
    setError(null);
    try {
      if (action === 'verify-docs') {
        const claim = await api.post<Claim>(`/api/v1/benefits/claims/${claimId}/verify-docs`);
        setResult({ kind: 'claim', claim });
      } else if (action === 'calculate') {
        const r = await api.post<{ claim: Claim; breakdown: FormulaBreakdown }>(`/api/v1/benefits/claims/${claimId}/calculate`, {
          startDate,
        });
        setResult({ kind: 'calc', claim: r.claim, breakdown: r.breakdown });
      } else {
        const account = await api.post<PensionAccount>(`/api/v1/benefits/claims/${claimId}/award`, {
          startDate,
          dob: dob || undefined,
        });
        setResult({ kind: 'account', account });
      }
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card mb-6 p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Process a claim</h2>
      <p className="mb-3 text-xs text-slate-500">Verify → calculate → award, by claim ID. Each step shows the API's response.</p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-3">
        <Field label="Claim ID">
          <input className="field-input" value={claimId} onChange={(e) => setClaimId(e.target.value)} placeholder="cuid from the member's claim" />
        </Field>
        <Field label="Start date (for calculate/award)">
          <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Claimant DOB (award, optional)">
          <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {canVerify ? (
          <button className="btn-secondary" onClick={() => run('verify-docs')} disabled={busy !== null}>
            {busy === 'verify-docs' ? 'Verifying…' : 'Verify documents'}
          </button>
        ) : null}
        {canVerify ? (
          <button className="btn-secondary" onClick={() => run('calculate')} disabled={busy !== null || !startDate}>
            {busy === 'calculate' ? 'Calculating…' : 'Calculate'}
          </button>
        ) : null}
        {canApprove ? (
          <button className="btn-primary" onClick={() => run('award')} disabled={busy !== null || !startDate}>
            {busy === 'award'
              ? 'Signing…'
              : `Award (sign as ${approverRoles.includes('SPF_APPROVER_A') ? 'Approver A' : 'Approver B'})`}
          </button>
        ) : null}
      </div>

      {result ? (
        <div className="mt-4 rounded-lg bg-slate-50 p-3">
          {result.kind === 'claim' ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono text-xs">{result.claim.refNumber}</span>
              <StatusBadge status={result.claim.status} />
            </div>
          ) : result.kind === 'calc' ? (
            <div>
              <div className="mb-1 flex items-center gap-2 text-sm">
                <span className="font-mono text-xs">{result.claim.refNumber}</span>
                <StatusBadge status={result.claim.status} />
                <span className="font-bold tabular-nums text-seagreen-700">{scr(result.breakdown.monthlyAmount)}/mo</span>
              </div>
              <ul className="list-inside list-disc space-y-0.5 text-xs text-slate-600">
                {result.breakdown.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-sm">
              <StatusBadge status={result.account.status} />
              <span className="ml-2 font-bold tabular-nums text-seagreen-700">{scr(result.account.monthlyAmount)}/mo</span>
              {result.account.status === 'SUSPENDED' && result.account.suspendReason === 'AWAITING_SECOND_APPROVAL' ? (
                <p className="mt-1 text-xs text-amber-700">
                  First signature recorded — a different approver must sign to release payment (four-eyes).
                </p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PayRunPanel() {
  const [period, setPeriod] = useState('');
  const [runId, setRunId] = useState('');
  const [valueDate, setValueDate] = useState('');
  const [debtorAccountId, setDebtorAccountId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const build = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const run = await api.post<{ id: string; period: string; totals: { pensioners: number; amount: number } }>(
        `/api/v1/benefits/payruns/${period}/build`,
      );
      setRunId(run.id);
      setMessage(`Pay run built for ${run.period}: ${run.totals.pensioners} pensioners, ${scr(run.totals.amount)}.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to build pay run');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/benefits/payruns/${runId}/send`, { valueDate, debtorAccountId });
      setMessage('Pay run sent to Treasury for settlement.');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to send pay run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-6 p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Monthly pension pay run</h2>
      <p className="mb-3 text-xs text-slate-500">Build the run from every IN_PAYMENT account, then send it to Treasury.</p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Period (YYYY-MM)">
          <input className="field-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07" />
        </Field>
        <Field label="Value date (for send)">
          <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
        </Field>
        <Field label="Debtor account ID (for send)">
          <input className="field-input" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)} placeholder="TSA bank account id" />
        </Field>
      </div>
      <div className="mt-3 flex gap-2">
        <button className="btn-secondary" onClick={build} disabled={busy || !period}>
          {busy ? 'Working…' : 'Build pay run'}
        </button>
        <button className="btn-primary" onClick={send} disabled={busy || !runId || !valueDate || !debtorAccountId}>
          Send to Treasury
        </button>
      </div>
    </div>
  );
}

function MaintenancePanel() {
  const [asOf, setAsOf] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ ceased: number }>('/api/v1/benefits/maintenance/age-limit', { asOf });
      setMessage(`${r.ceased} children's pension(s) ceased at the age limit.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sweep failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Children's age-limit sweep</h2>
      <p className="mb-3 text-xs text-slate-500">Ceases CHILDRENS pensions once the beneficiary passes the configured age limit.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <Field label="As of date">
        <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </Field>
      <button className="btn-secondary mt-3" onClick={run} disabled={busy || !asOf}>
        {busy ? 'Running…' : 'Run sweep'}
      </button>
    </div>
  );
}

function AnnualRecalcPanel() {
  const [upliftPct, setUpliftPct] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ recalculated: number }>('/api/v1/benefits/recalculations/annual', {
        upliftPct: Number.parseFloat(upliftPct),
        reason,
      });
      setMessage(`${r.recalculated} pension(s) uplifted.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Recalculation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Annual pension recalculation</h2>
      <p className="mb-3 text-xs text-slate-500">SPF_ADMIN only. Uplifts every IN_PAYMENT pension by a flat percentage.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Uplift %">
          <input className="field-input" type="number" step="0.1" value={upliftPct} onChange={(e) => setUpliftPct(e.target.value)} />
        </Field>
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Cost of living adjustment 2026" />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={run} disabled={busy || !upliftPct || reason.length < 3}>
        {busy ? 'Applying…' : 'Apply recalculation'}
      </button>
    </div>
  );
}

function RefundPanel() {
  const [nin, setNin] = useState('');
  const [basis, setBasis] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ id: string }>('/api/v1/benefits/refunds', { nin, basis, amount: Number.parseFloat(amount) });
      setMessage(`Refund claim ${r.id} registered.`);
      setNin('');
      setBasis('');
      setAmount('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register refund claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mt-6 p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Contribution refund claims</h2>
      <p className="mb-3 text-xs text-slate-500">e.g. a member who never reached the qualifying period.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-3 gap-3">
        <Field label="NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Basis">
          <input className="field-input" value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="e.g. Left Seychelles permanently, under qualifying period" />
        </Field>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !nin || basis.length < 3 || !amount}>
        {busy ? 'Registering…' : 'Register refund claim'}
      </button>
    </div>
  );
}
